import React, { useState, useRef, useEffect } from 'react';
import { Drawer, Box, Typography, TextField, IconButton, Paper, CircularProgress, Button, Divider } from '@mui/material';
import { Close, Send, ContentPaste } from '@mui/icons-material';
import Markdown from 'react-markdown';
import { chatService, proposalService } from '../services/api';

export default function ChatPanel({ open, onClose, onStartProposal }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hi! I can help with **cloud architecture questions** or you can **paste RFP text** here to generate a technical proposal.\n\nTry asking about AWS services, architecture patterns, or paste your requirements text.' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pasteMode, setPasteMode] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const res = await chatService.send(msg);
      setMessages(prev => [...prev, { role: 'assistant', content: res.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${err.response?.data?.detail || err.message}` }]);
    } finally { setLoading(false); }
  };

  const handlePasteGenerate = async () => {
    const text = pasteText.trim();
    if (!text) return;
    setPasteMode(false);
    setMessages(prev => [...prev, { role: 'user', content: `[Pasted RFP text — ${text.length} chars]` }]);
    setLoading(true);
    try {
      // Upload as text, then trigger proposal flow
      const blob = new Blob([text], { type: 'text/plain' });
      const file = new File([blob], 'pasted_rfp.txt', { type: 'text/plain' });
      const result = await proposalService.upload([file]);
      setMessages(prev => [...prev, { role: 'assistant', content: `✅ Text uploaded (${result.text_length} chars). Starting proposal workflow...` }]);
      setPasteText('');
      onStartProposal(result);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Failed to process text: ${err.message}` }]);
    } finally { setLoading(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { width: 420, display: 'flex', flexDirection: 'column' } }}>
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E2E8F0' }}>
        <Typography variant="subtitle1" fontWeight={700}>💬 Cloud Architecture Chat</Typography>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {messages.map((msg, i) => (
          <Paper key={i} elevation={0} sx={{
            p: 1.5, maxWidth: '85%', borderRadius: 2,
            alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
            bgcolor: msg.role === 'user' ? '#6D28D9' : '#F1F5F9',
            color: msg.role === 'user' ? '#fff' : '#1E293B',
            '& p': { m: 0 }, '& code': { fontSize: 12 },
          }}>
            <Markdown>{msg.content}</Markdown>
          </Paper>
        ))}
        {loading && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', color: '#64748B' }}>
            <CircularProgress size={16} /> <Typography variant="caption">Thinking...</Typography>
          </Box>
        )}
        <div ref={bottomRef} />
      </Box>

      <Divider />

      {/* Paste Mode */}
      {pasteMode && (
        <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderTop: '1px solid #E2E8F0' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Paste your RFP/requirements text below:
          </Typography>
          <TextField multiline rows={6} fullWidth value={pasteText} onChange={e => setPasteText(e.target.value)}
            placeholder="Paste RFP content here..." size="small"
            sx={{ '& .MuiOutlinedInput-root': { fontSize: 12 } }} />
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button size="small" onClick={() => setPasteMode(false)}>Cancel</Button>
            <Button size="small" variant="contained" onClick={handlePasteGenerate} disabled={!pasteText.trim() || loading}
              sx={{ bgcolor: '#6D28D9' }}>Generate Proposal</Button>
          </Box>
        </Box>
      )}

      {/* Input */}
      <Box sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'flex-end', borderTop: '1px solid #E2E8F0' }}>
        <IconButton size="small" onClick={() => setPasteMode(!pasteMode)} color={pasteMode ? 'primary' : 'default'}
          title="Paste text to generate proposal"><ContentPaste fontSize="small" /></IconButton>
        <TextField fullWidth size="small" multiline maxRows={3} value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown} placeholder="Ask about AWS architecture..."
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
        <IconButton onClick={handleSend} disabled={!input.trim() || loading} color="primary"><Send /></IconButton>
      </Box>
    </Drawer>
  );
}
